import React, { useEffect, useMemo, useState } from "react";
import api from "../../api/client";
import { ExpandableLeafletMap } from "../ExpandableLeafletMap";
import {
  DAYS,
  toDateStr,
  parseDateOnly,
  parseDateTime,
  startOfWeekMonday,
  endOfWeekSunday,
  formatNiceDate,
  formatNiceTime,
  isWithin,
  fromDbTime,
  pointToLatLng,
  googleMapsLink,
} from "../../utils/schedule";

export const SchedulePage = () => {
  const [weekAnchor, setWeekAnchor] = useState(() => new Date());
  const [selectedDayIndex, setSelectedDayIndex] = useState(-1);
  const [selectedStopId, setSelectedStopId] = useState(null);
  const [selectVersion, setSelectVersion] = useState(0);

  const [locationsById, setLocationsById] = useState({});
  const [scheduleRows, setScheduleRows] = useState([]);
  const [upcomingRows, setUpcomingRows] = useState([]);

  const [error, setError] = useState("");

  const weekStart = useMemo(() => startOfWeekMonday(weekAnchor), [weekAnchor]);
  const weekEnd = useMemo(() => endOfWeekSunday(weekAnchor), [weekAnchor]);

  const weekDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < 7; i += 1) {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      days.push(d);
    }
    return days;
  }, [weekStart]);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const res = await api.get("/locations");
        const items = Array.isArray(res.data) ? res.data : [];
        const nextMap = {};
        items.forEach((location) => {
          nextMap[location.id] = location;
        });
        setLocationsById(nextMap);
      } catch (e) {
        console.error(e);
      }
    };

    const fetchUpcoming = async () => {
      try {
        const today = toDateStr(new Date());
        const twoWeeksOut = new Date();
        twoWeeksOut.setDate(twoWeeksOut.getDate() + 14);
        const res = await api.get("/schedule", { params: { start: today, end: toDateStr(twoWeeksOut) } });
        setUpcomingRows(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        console.error(e);
      }
    };

    fetchLocations();
    fetchUpcoming();
  }, []);

  useEffect(() => {
    const fetchSchedule = async () => {
      try {
        setError("");
        const start = toDateStr(weekStart);
        const end = toDateStr(weekEnd);
        const res = await api.get("/schedule", { params: { start, end } });
        setScheduleRows(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        console.error(e);
        setError("Failed to load schedule");
        setScheduleRows([]);
      }
    };

    fetchSchedule();
  }, [weekStart, weekEnd]);

  const weekStops = useMemo(() => {
    const stops = [];

    for (const row of scheduleRows) {
      const loc = locationsById[row.location] || row.locationObj || null;
      const locName = loc?.name || row.location_name || "Stop";
      const address = loc?.address || row.location_address || "";
      const details = loc?.details || row.location_details || "";
      const { lat, lng } = pointToLatLng({
        lat: loc?.lat ?? row.location_lat,
        lng: loc?.lng ?? row.location_lng,
        coords: loc?.coords,
      });

      const startD = parseDateOnly(row.start_date);
      const endD = parseDateOnly(row.end_date);
      if (!startD || !endD) continue;

      for (let d = new Date(startD); d <= endD; d.setDate(d.getDate() + 1)) {
        const dateStr = toDateStr(d);
        const id = `${row.id}_${dateStr}`;

        stops.push({
          id,
          scheduleId: row.id,
          locationId: row.location,
          title: locName,
          date: dateStr,
          startTime: fromDbTime(row.daily_start_time),
          endTime: fromDbTime(row.daily_end_time),
          address,
          lat: lat || "",
          lng: lng || "",
          notes: details,
          status: "scheduled",
        });
      }
    }

    return stops.sort((a, b) => {
      const aStart = parseDateTime(a.date, a.startTime).getTime();
      const bStart = parseDateTime(b.date, b.startTime).getTime();
      return aStart - bStart;
    });
  }, [scheduleRows, locationsById]);

  const visibleStops = useMemo(() => {
    if (selectedDayIndex === -1) return weekStops;

    return weekStops.filter((stop) => {
      const d = parseDateTime(stop.date, stop.startTime);
      const dayIdx = (d.getDay() + 6) % 7;
      return dayIdx === selectedDayIndex;
    });
  }, [weekStops, selectedDayIndex]);

  const nowCard = useMemo(() => {
    const now = new Date();
    const upcoming = [];

    for (const row of upcomingRows) {
      const loc = locationsById[row.location] || null;
      const { lat, lng } = pointToLatLng({
        lat: loc?.lat ?? row.location_lat,
        lng: loc?.lng ?? row.location_lng,
        coords: loc?.coords,
      });
      const startD = parseDateOnly(row.start_date);
      const endD = parseDateOnly(row.end_date);
      if (!startD || !endD) continue;

      for (let d = new Date(startD); d <= endD; d.setDate(d.getDate() + 1)) {
        const dateStr = toDateStr(d);
        upcoming.push({
          stop: {
            id: `${row.id}_${dateStr}`,
            title: loc?.name || row.location_name || "Stop",
            address: loc?.address || row.location_address || "",
            lat: lat || "",
            lng: lng || "",
            notes: loc?.details || row.location_details || "",
          },
          start: parseDateTime(dateStr, fromDbTime(row.daily_start_time)),
          end: parseDateTime(dateStr, fromDbTime(row.daily_end_time)),
        });
      }
    }

    upcoming.sort((a, b) => a.start - b.start);

    const current = upcoming.find((entry) => isWithin(now, entry.start, entry.end));
    if (current) return { type: "now", ...current };

    const next = upcoming.find((entry) => entry.start > now);
    if (next) return { type: "next", ...next };

    return { type: "none" };
  }, [upcomingRows, locationsById]);

  const weekGrid = useMemo(() => {
    const groups = Array.from({ length: 7 }, () => []);
    for (const stop of weekStops) {
      const d = parseDateTime(stop.date, stop.startTime);
      const dayIdx = (d.getDay() + 6) % 7;
      groups[dayIdx].push(stop);
    }
    return groups;
  }, [weekStops]);

  const selectedStop = useMemo(
    () => weekStops.find((stop) => stop.id === selectedStopId) || null,
    [weekStops, selectedStopId]
  );

  function selectStop(id) {
    setSelectedStopId(id);
    setSelectVersion((v) => v + 1);
  }

  function shiftWeek(deltaWeeks) {
    setWeekAnchor((prev) => {
      const d = new Date(prev);
      d.setDate(d.getDate() + deltaWeeks * 7);
      return d;
    });
    setSelectedDayIndex(-1);
    setSelectedStopId(null);
  }

  function jumpToThisWeek() {
    setWeekAnchor(new Date());
    setSelectedDayIndex(-1);
    setSelectedStopId(null);
  }

  return (
    <div className="sp-wrap">
      <header className="sp-header">
        <div>
          <h1 className="sp-title">Where to find us</h1>
          <p className="sp-subtitle">
            Check this week's schedule and tap a stop to get directions.
          </p>
          {error ? <p className="sp-subtitle" style={{ color: "crimson" }}>{error}</p> : null}
        </div>

        <div className="sp-headerActions">
          <button className="sp-btn" type="button" onClick={jumpToThisWeek}>
            This week
          </button>
        </div>
      </header>

      <section className="sp-nowCard">
        {nowCard.type === "now" && (
          <>
            <div className="sp-nowBadge sp-badgeNow">Happening now</div>
            <div className="sp-nowTitle">{nowCard.stop.title}</div>
            <div className="sp-nowMeta">
              <span>{formatNiceDate(nowCard.start)}</span>
              <span className="sp-dot">&bull;</span>
              <span>
                {formatNiceTime(nowCard.start)}-{formatNiceTime(nowCard.end)}
              </span>
              <span className="sp-dot">&bull;</span>
              <span className="sp-addr">{nowCard.stop.address || "Address TBD"}</span>
            </div>
            <div className="sp-nowActions">
              <a
                className="btn-cta"
                href={googleMapsLink(nowCard.stop.address, nowCard.stop.lat, nowCard.stop.lng)}
                target="_blank"
                rel="noreferrer"
              >
                Get directions
              </a>
            </div>
          </>
        )}

        {nowCard.type === "next" && (
          <>
            <div className="sp-nowBadge sp-badgeNext">Next stop</div>
            <div className="sp-nowTitle">{nowCard.stop.title}</div>
            <div className="sp-nowMeta">
              <span>{formatNiceDate(nowCard.start)}</span>
              <span className="sp-dot">&bull;</span>
              <span>
                {formatNiceTime(nowCard.start)}-{formatNiceTime(nowCard.end)}
              </span>
              <span className="sp-dot">&bull;</span>
              <span className="sp-addr">{nowCard.stop.address || "Address TBD"}</span>
            </div>
            <div className="sp-nowActions">
              <a
                className="btn-cta"
                href={googleMapsLink(nowCard.stop.address, nowCard.stop.lat, nowCard.stop.lng)}
                target="_blank"
                rel="noreferrer"
              >
                Get directions
              </a>
            </div>
          </>
        )}

        {nowCard.type === "none" && (
          <>
            <div className="sp-nowBadge sp-badgeNone">No upcoming stops</div>
            <div className="sp-nowTitle">No schedule posted for this week yet.</div>
            <div className="sp-nowMeta">Check back soon.</div>
          </>
        )}
      </section>

      <main className="sp-main">
        <section className="sp-card full-width">
          <div className="sp-cardHeader">
            <h2 className="sp-cardTitle">Week at a glance</h2>
            <div className="sp-weekNav">
              <button className="sp-weekBtn" type="button" onClick={() => shiftWeek(-1)} aria-label="Previous week">
                &larr;
              </button>
              <div className="sp-weekRange">
                {weekStart.toLocaleDateString(undefined, { month: "short", day: "numeric" })} -{" "}
                {weekEnd.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
              </div>
              <button className="sp-weekBtn" type="button" onClick={() => shiftWeek(1)} aria-label="Next week">
                &rarr;
              </button>
            </div>
          </div>

          <div className="sp-weekGrid">
            {weekDays.map((dayDate, idx) => {
              const items = weekGrid[idx];
              return (
                <button
                  key={dayDate.toISOString()}
                  type="button"
                  className={`sp-dayCol ${selectedDayIndex === idx ? "is-selected" : ""}`}
                  onClick={() => {
                    setSelectedDayIndex(selectedDayIndex === idx ? -1 : idx);
                    setSelectedStopId(null);
                  }}
                >
                  <div className="sp-dayTop">
                    <div className="sp-dayName">{DAYS[idx]}</div>
                    <div className="sp-dayDate">
                      {dayDate.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                    </div>
                  </div>

                  <div className="sp-dayItems">
                    {items.length === 0 ? (
                      <div className="sp-empty">No stops</div>
                    ) : (
                      items.slice(0, 3).map((stop) => {
                        const start = parseDateTime(stop.date, stop.startTime);
                        const end = parseDateTime(stop.date, stop.endTime);
                        return (
                          <div className="sp-miniItem" key={stop.id}>
                            <div className="sp-miniTime">
                              {formatNiceTime(start)}-{formatNiceTime(end)}
                            </div>
                            <div className="sp-miniTitle">{stop.title}</div>
                          </div>
                        );
                      })
                    )}
                    {items.length > 3 && <div className="sp-more">+{items.length - 3} more</div>}
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        <section className="sp-card">
          <div className="sp-cardHeader">
            <h2 className="sp-cardTitle">Stops</h2>
            <p className="sp-cardNote">{visibleStops.length} listed</p>
          </div>

          <div className="sp-stopList">
            {visibleStops.length === 0 ? (
              <div className="sp-emptyBig">No stops posted for this filter.</div>
            ) : (
              visibleStops.map((stop) => {
                const start = parseDateTime(stop.date, stop.startTime);
                const end = parseDateTime(stop.date, stop.endTime);
                const isSelected = selectedStopId === stop.id;
                return (
                  <button
                    key={stop.id}
                    type="button"
                    className={`sp-stopRow ${isSelected ? "is-selected" : ""}`}
                    onClick={() => selectStop(stop.id)}
                  >
                    <div className="sp-stopTitle">{stop.title}</div>
                    <div className="sp-stopMeta">
                      <span>{formatNiceDate(start)}</span>
                      <span className="sp-dot">&bull;</span>
                      <span>
                        {formatNiceTime(start)}-{formatNiceTime(end)}
                      </span>
                    </div>
                    <div className="sp-stopAddr">{stop.address || "Address TBD"}</div>
                    {stop.notes ? <div className="sp-stopNotes">{stop.notes}</div> : null}

                    <div className="sp-stopActions">
                      <a
                        className="sp-link"
                        href={googleMapsLink(stop.address, stop.lat, stop.lng)}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Directions
                      </a>
                      <span className="sp-sep">&middot;</span>
                      <span className="sp-linkHint">Tap to highlight on map</span>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </section>

        <section className="sp-card sp-mapCard">
          <div className="sp-cardHeader">
            <h2 className="sp-cardTitle">Map</h2>
            <p className="sp-cardNote">Expand for a larger map view.</p>
          </div>

          <ExpandableLeafletMap
            markers={visibleStops}
            selectedId={selectedStop?.id || null}
            selectVersion={selectVersion}
            onSelect={selectStop}
            emptyTitle="No mapped stops for this filter"
            emptyText="Select another day or add coordinates to a location to preview it here."
          />

          <div className="sp-mapDetails">
            {!selectedStop ? (
              <div className="sp-mapEmpty">
                <div className="sp-mapTitle">Select a stop to preview</div>
                <div className="sp-mapText">
                  The map shows all visible stops with coordinates. Pick one from the list to center the map on it.
                </div>
              </div>
            ) : (
              <div className="sp-mapSelected">
                <div className="sp-mapBadge">Selected</div>
                <div className="sp-mapSelectedTitle">{selectedStop.title}</div>

                <div className="sp-mapSelectedMeta">
                  <span>{selectedStop.address || "Address TBD"}</span>
                  {(selectedStop.lat || selectedStop.lng) ? (
                    <>
                      <span className="sp-dot">&bull;</span>
                      <span>
                        {selectedStop.lat || "--"}, {selectedStop.lng || "--"}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="sp-dot">&bull;</span>
                      <span>Map pin unavailable for this stop yet</span>
                    </>
                  )}
                </div>

                {selectedStop.notes ? <div className="sp-mapSelectedNotes">{selectedStop.notes}</div> : null}

                <div className="sp-mapActions">
                  <a
                    className="btn-cta"
                    href={googleMapsLink(selectedStop.address, selectedStop.lat, selectedStop.lng)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Get directions
                  </a>
                  <button className="sp-btn" type="button" onClick={() => setSelectedStopId(null)}>
                    Clear selection
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>
      </main>

      <footer className="sp-footer">
        <div className="sp-footNote">
          Hours and locations may change due to weather, events, or supply.
        </div>
      </footer>
    </div>
  );
};
